import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Notification } from '@models/notification';
import { ResultsetItem } from '@models/resultset-item';
import { TableDataService } from '@services/lookup-tables/table-data.service';
import { MenuService } from '@services/menu/menu.service';
import { NotificationsService } from '@services/notifications/notifications.service';
import { ApplicationStatus } from '@services/results/application-status';
import { HIGHLIGHTS_TAB, ResultTabsService, View, ViewType } from '@services/results/result-tabs.service';
import { ResultsStoreService } from '@services/results/results-store.service';
import { ResultsService } from '@services/results/results.service';
import { SettingsService } from '@services/settings/settings.service';
import { fromEvent, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import SimpleBar from 'simplebar';
import { ExportService } from '../export/export.service';

interface ResultsetItemWithDisabled extends ResultsetItem {
  isTabDisabled?: boolean;
}

@Component({
  selector: 'app-results-footer-bar',
  templateUrl: './footer-bar.component.html',
  styleUrls: ['./footer-bar.component.scss'],
})
export class FooterBarComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('footertabs', { static: false, read: ElementRef }) simpleBar: ElementRef;
  @ViewChild('result_tab_container', { static: false }) resultTabContainer: ElementRef;
  @ViewChild('btnPanLeft', { static: false }) btnPanLeft: ElementRef;
  @ViewChild('btnPanRight', { static: false }) btnPanRight: ElementRef;
  @ViewChild('footer_controls', { static: false }) footerControls: ElementRef;

  public sheetListVisible = false;

  public get reportSummaryVisible() {
    return this.tabsService.activeView.tabName === HIGHLIGHTS_TAB;
  }

  public hidePanButtons: boolean;
  public resultsTabs: ResultsetItemWithDisabled[];
  public exporting = false;
  public running = false;
  public visibleTabsLength = 0;
  public isResultsetBeingLoaded$ = this.resultsStore.isFetchingSingleResultset$;
  private notificationList: Notification[];
  private ngOndestroy$ = new Subject();

  public get isExportDisabled() {
    return this.exporting || this.running || this.visibleTabsLength === 0;
  }

  constructor(
    private dialog: MatDialog,
    private elem: ElementRef,
    private menu: MenuService,
    private ngZone: NgZone,
    private notifications: NotificationsService,
    private resultsStore: ResultsStoreService,
    private readonly exportsService: ExportService,
    public tabsService: ResultTabsService,
    public resultsService: ResultsService,
    public settingsService: SettingsService,
    public tableDataService: TableDataService
  ) {}

  public isActiveTab(tab: ResultsetItem) {
    const currentView = this.tabsService.activeView;
    return currentView.tabName === tab.Mnemonic;
  }

  ngOnInit() {
    // Subscriptions
    this.tabsService.tabCollection$.pipe(takeUntil(this.ngOndestroy$)).subscribe((tabs: ResultsetItem[]) => {
      this.resultsTabs = tabs;
      this.visibleTabsLength = tabs.filter(tab => !tab.Hidden && !tab.IsLoading).length;
      this.menu.emitResize();
    });

    this.notifications.notificationList$.pipe(takeUntil(this.ngOndestroy$)).subscribe(_ => {
      this.notificationList = [
        ...Array.from(this.notifications.getNotificationListByType('resultError').values()),
        ...Array.from(this.notifications.getNotificationListByType('resultset-ood').values()),
      ];
    });

    this.resultsStore.appStatus$.pipe(takeUntil(this.ngOndestroy$)).subscribe((status: ApplicationStatus) => {
      if (status === ApplicationStatus.RunningSimulation) {
        this.running = true;
      } else if (status === ApplicationStatus.DoneSimulation) {
        this.running = false;
      }
    });
  }

  ngAfterViewInit() {
    const scrollBar = new SimpleBar(this.simpleBar.nativeElement);

    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        // this.simpleBar is the ElementRef of simplebar element retrived via @ViewChild
        // assuming you want to add a listener
        scrollBar.getScrollElement().addEventListener('scroll', event => {
          // do your thing
        });
        this.calculateVisiblePanControls();
      });
    });

    fromEvent(document, 'click')
      .pipe(takeUntil(this.ngOndestroy$))
      .subscribe((e: MouseEvent) => {
        if (!(<HTMLInputElement>e.target).closest('.footer_quick_link') && this.sheetListVisible) {
          this.sheetListVisible = false;
        }
      });

    fromEvent(window, 'resize')
      .pipe(takeUntil(this.ngOndestroy$))
      .subscribe(_ => {
        if (this.dialog && this.dialog.openDialogs.length === 0) {
          this.calculateVisiblePanControls();
        }
      });
  }

  ngOnDestroy(): void {
    this.ngOndestroy$.next();
    this.ngOndestroy$.complete();
  }

  public getNotificationByMnemonic(mnemonic): Notification {
    return this.notificationList.find((notification: Notification) => notification.references.mnemonic === mnemonic);
  }

  public hasNotificationByMnemonic(mnemonic: string): boolean {
    const hasNotificationsInList =
      this.notificationList.filter((notification: Notification) => notification.references.mnemonic === mnemonic).length > 0;
    const hasNotificationsInResultset = this.resultsTabs.filter(t => t.HasNotifications && t.Mnemonic === mnemonic).length > 0;
    return hasNotificationsInList || hasNotificationsInResultset;
  }

  public toggleSheetList() {
    if (this.resultsTabs && this.resultsTabs.length > 0) {
      this.sheetListVisible = !this.sheetListVisible;
    }
  }

  public toggleReportSummary() {
    if (this.resultsService.spread.getSheetFromName(HIGHLIGHTS_TAB) !== null) {
      this.tabsService.selectTab({ tabName: HIGHLIGHTS_TAB });
    }
  }

  private disableLocalTabs(target: ResultsetItemWithDisabled) {
    for (const base of this.resultsTabs) {
      if (target.Is_Stress && base.RunID === target.RunID_Used) {
        for (const st of base.StressList as ResultsetItemWithDisabled[]) {
          if (st.RunID === target.RunID) {
            st.isTabDisabled = true;
          }
        }
      } else if (base.RunID === target.RunID) {
        base.isTabDisabled = true;
        for (const st of base.StressList as ResultsetItemWithDisabled[]) {
          st.isTabDisabled = true;
        }
      }
    }
  }

  public hideTabs(target: ResultsetItemWithDisabled) {
    if (target && !target.IsLoading && !target.isTabDisabled) {
      this.disableLocalTabs(target);
      this.resultsStore.hideShowResultSet(target, true);
    }
  }

  public async exportSheets() {
    this.exporting = true;
    await this.exportsService.exportSheets().finally(() => (this.exporting = false));
  }

  public handleActiveSheet(event, resultsetItem) {
    const target = event.target || event.srcElement || event.currentTarget;
    if (target.classList.contains('prevent')) {
      event.preventDefault();
      return;
    }
    if (!resultsetItem.InMemory) {
      this.resultsStore.getSingleResultset(resultsetItem.RunID).then(result => {
        const updatedResult = { ...result, InMemory: true };
        this.resultsStore.updateByUUID(result.RunID, updatedResult, updatedResult);
        this.activateTab(event.target.innerText);
      });
    } else {
      this.activateTab(event.target.innerText);
    }
  }

  private activateTab(tabName: any) {
    const selectedSwitchState = (this.tabsService.activeView.type === 'summary' ? 'summary' : 'detail') as ViewType;
    this.tabsService.selectTab({
      tabName: `${tabName}`,
      type: selectedSwitchState,
    } as View);
    this.sheetListVisible = false;

    setTimeout(() => {
      const activeTabElement = this.elem.nativeElement.querySelector('.btn_sheet_active');
      if (activeTabElement) activeTabElement.scrollIntoView(false);
    });
  }

  public onClickPanButtons(event) {
    const target = event.target || event.srcElement || event.currentTarget;
    const idAttr = target.attributes.id;
    const value = idAttr.nodeValue;
    const scrollBar = new SimpleBar(this.simpleBar.nativeElement);
    const scroll = scrollBar.getScrollElement();
    switch (value) {
      case 'btn_pan_right':
        scroll.scrollLeft += 200;
        break;
      case 'btn_pan_left':
        scroll.scrollLeft -= 200;
        break;
      default:
        console.error('The scrollbar controls have changed IDs, please check with HTML developer.');
    }
    this.togglePanControls();
  }

  public togglePanControls() {
    const scrollBar = new SimpleBar(this.simpleBar.nativeElement);
    const scroll = scrollBar.getScrollElement();
    const newScrollLeft = scroll.scrollLeft;
    const width = this.simpleBar.nativeElement.offsetWidth;
    const scrollWith = scroll.children[0].children[0].scrollWidth;

    this.btnPanRight.nativeElement.disabled = scrollWith - newScrollLeft - width <= 1;
    this.btnPanLeft.nativeElement.disabled = newScrollLeft === 0;
  }

  public calculateVisiblePanControls() {
    const resultWidth = this.resultTabContainer.nativeElement.offsetWidth;
    const tabsWidth = this.resultTabContainer.nativeElement.children[0].offsetWidth;
    this.footerControls.nativeElement.hidden = resultWidth >= tabsWidth;
  }

  public getScenarioColor(resultItem: ResultsetItem) {
    return this.settingsService.getScenarioColor(resultItem);
  }
}
